import _ from "lodash";
import React, { useEffect } from "react";
import { ReactElement, ReactNode } from "react";

export type Breadcrumb = {
  label: string;
  path: string;
  backArrow?: boolean;
};

export type BreadcrumbOptions = {
  hideSubaccountTitle?: boolean;
};

export type BreadcrumbContextValue = {
  breadcrumbs: Breadcrumb[];
  options: BreadcrumbOptions;
  setBreadcrumbs: (
    breadcrumbs: Breadcrumb[],
    options?: BreadcrumbOptions
  ) => void;
};

export const BreadcrumbContext = React.createContext<BreadcrumbContextValue>({
  breadcrumbs: [],
  options: {},
  setBreadcrumbs: () => {
    /* noop */
  },
});

export function useBreadcrumbs(
  setBreadcrumbs?: Breadcrumb[],
  options?: BreadcrumbOptions
): { breadcrumbs: Breadcrumb[]; options: BreadcrumbOptions } {
  const ctx = React.useContext(BreadcrumbContext);
  useEffect(() => {
    if (
      setBreadcrumbs &&
      (!_.isEqual(ctx.breadcrumbs, setBreadcrumbs) ||
        !_.isEqual(ctx.options, options || {}))
    ) {
      ctx.setBreadcrumbs(setBreadcrumbs, options);
      document.title = `Scale to Win Text | ${setBreadcrumbs.map(b => b.label).join(" | ")}`;
    }
  });

  return {
    breadcrumbs: setBreadcrumbs || ctx.breadcrumbs,
    options: options || ctx.options,
  };
}

export default function BreadcrumbsProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [breadcrumbs, setBreadcrumbs] = React.useState<Breadcrumb[]>([]);
  const [options, setOptions] = React.useState<BreadcrumbOptions>({});

  const setBreadcrumbsAndOptions = (
    breadcrumbs: Breadcrumb[],
    givenOptions: BreadcrumbOptions = {}
  ) => {
    setBreadcrumbs(breadcrumbs);
    setOptions(givenOptions);
  };

  return (
    <BreadcrumbContext.Provider
      value={{ breadcrumbs, options, setBreadcrumbs: setBreadcrumbsAndOptions }}
    >
      {children}
    </BreadcrumbContext.Provider>
  );
}
